<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import store from "@/store";
import trim from "lodash-es/trim";

import { toast } from "@/common/utils/toast";
import { getDate, getHour } from "@/common/utils/dates";

import {
  consoleGameList,
  consoleGameListId,
  consoleGameCreate,
  consoleGameDelete,
} from "@/common/api/rest/console/game";

import { gameList } from "@/common/api/rest/game/game";

import type { ConsoleGameType } from "@/common/types/console/game";
import type { GameType } from "@/common/types/game/game";

import {
  baseAvatar,
  baseConfirm,
  baseLoading,
  baseInput,
  baseModal,
  baseTable,
  svgIcon,
} from "@/common/components";

const { t, locale } = useI18n();

const props = defineProps<{
  consoleId: string;
}>();

const imageSize = "15rem";

const games = ref<ConsoleGameType[]>([]);
const gameIds = ref<{ item_id: string; game_id: string }[]>([]);
const selected = ref<ConsoleGameType | null>(null);

const limit = ref<number>(5);
const page = ref<number>(0);
const pages = ref<number>(0);
const search = ref<string>("");
const searched = ref<number>(0);
const total = ref<number>(0);

const loading = ref(true);
const showModalAddGame = ref(false);
const showModalRemoveGame = ref(false);

const gameSearchListRef = ref<any>(null);
const gameSearch = ref<string>("");
const gameSearchList = ref<GameType[]>([]);
const gameLimit = ref<number>(10);
const gamePage = ref<number>(0);
const gamePages = ref<number>(0);
const gameSearched = ref<number>(0);
const gameTotal = ref<number>(0);
const loadingMoreGames = ref(false);

const tableHeader = computed(() => [
  { key: "avatar", label: "" },
  { key: "name", label: t("common.labels.name") },
  { key: "created", label: t("common.labels.created") },
]);

async function fetchGames() {
  await consoleGameList(props.consoleId, {
    page: page.value,
    limit: limit.value,
    search: search.value,
    sort: "DESC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        games.value = data.list;
        pages.value = data.infos.pages;
        total.value = data.infos.total;
        searched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function fetchGameId() {
  await consoleGameListId(props.consoleId)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        gameIds.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

async function searchGames(append = false) {
  if (
    append &&
    (loadingMoreGames.value || gamePage.value + 1 >= gamePages.value)
  ) {
    return;
  }

  if (append) {
    loadingMoreGames.value = true;
  }

  const pageToFetch = append ? gamePage.value + 1 : 0;

  await gameList({
    page: pageToFetch,
    limit: gameLimit.value,
    search: gameSearch.value,
    sort: "ASC",
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const newItems = data.list ?? [];
        gameSearchList.value = append
          ? [...gameSearchList.value, ...newItems]
          : newItems;
        gamePage.value = pageToFetch;
        gamePages.value = data.infos.pages;
        gameTotal.value = data.infos.total;
        gameSearched.value = data.infos.count;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      loadingMoreGames.value = false;
    });
}

function changePage(value: number) {
  page.value = value;
  fetchGames();
}

function searchItem(value: string) {
  search.value = value;

  fetchGames();
}

function resetSearchGame() {
  gameSearch.value = "";
  gameSearchList.value = [];
  gamePage.value = 0;
  gamePages.value = 0;
  gameSearched.value = 0;
  gameTotal.value = 0;
  loadingMoreGames.value = false;
}

async function addGame() {
  store.commit("SET_LOADING", true);

  resetSearchGame();
  await searchGames();
  store.commit("SET_LOADING", false);

  showModalAddGame.value = true;
}

async function searchGame() {
  gameSearchList.value = [];
  gamePage.value = 0;
  gamePages.value = 0;
  gameSearched.value = 0;
  gameTotal.value = 0;

  await searchGames();
}

function onGameListScroll(event: Event) {
  const target =
    (event.target as HTMLElement | null) ?? gameSearchListRef.value;

  if (!target) {
    return;
  }

  const reachedEnd =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

  if (
    reachedEnd &&
    !loadingMoreGames.value &&
    gamePage.value + 1 < gamePages.value
  ) {
    void searchGames(true);
  }
}

async function selectGame(game: GameType) {
  if (!gameIds.value.some((g) => g.game_id === game.game_id)) {
    await consoleGameCreate(props.consoleId, { game_id: game.game_id })
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchGameId();
          fetchGames();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  } else {
    const item = gameIds.value.find((g) => g.game_id === game.game_id);

    await consoleGameDelete(item.item_id)
      .then((response) => {
        const { success, message } = response;

        if (success) {
          fetchGameId();
          fetchGames();
        } else {
          toast({ message: message, type: "error" });
        }
      })
      .catch(() => {
        toast({ message: t("error.default"), type: "error" });
      });
  }
}

function removeGame(game: ConsoleGameType) {
  selected.value = game;
  showModalRemoveGame.value = true;
}

function removeGameCancel() {
  selected.value = null;
  showModalRemoveGame.value = false;
}

async function removeGameConfirm() {
  if (!selected.value) {
    return;
  }

  await consoleGameDelete(selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchGameId();
        fetchGames();
        showModalRemoveGame.value = false;
        selected.value = null;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

watch(
  () => gameSearch.value,
  () => {
    if (trim(gameSearch.value).length === 0) {
      searchGame();
    }
  },
);

onMounted(async () => {
  await fetchGames();
  await fetchGameId();
  loading.value = false;
});
</script>

<template>
  <section class="tab-console">
    <div class="tab-header">
      <svg-icon icon="no-image" size="2rem" />
      {{ t("collection.labels.games") }}
    </div>

    <div class="tab-content">
      <template v-if="loading">
        <div class="empty-data">
          <base-loading />
        </div>
      </template>
      <template v-else>
        <base-table
          :header="tableHeader"
          :data="games"
          :limit="limit"
          :page="page"
          :pages="pages"
          :total="total"
          :searched="searched"
          :options="true"
          :copy="false"
          :edit="false"
          :del="true"
          @change:page="changePage($event)"
          @searching="searchItem($event)"
          @btn:add="addGame"
          @btn:del="removeGame($event)"
        >
          <template #avatar="{ data }">
            <base-avatar
              position="center"
              :is-edit="false"
              :show-image="true"
              :url="data?.avatar?.large"
              :aspect-ratio="1.333"
              size="3rem"
              border-radius="0.25rem"
            />
          </template>

          <template #created="{ data }">
            <span>
              {{ getDate(data.created) }} - {{ getHour(data.created) }}
            </span>
          </template>
        </base-table>
      </template>
    </div>
  </section>

  <base-confirm
    :show="showModalRemoveGame"
    :title="t('collection.modals.titles.del_game').toUpperCase()"
    :message="
      t('collection.modals.messages.del_game', {
        name: selected?.name?.toUpperCase(),
      })
    "
    @cancel="removeGameCancel()"
    @confirm="removeGameConfirm()"
  />

  <base-modal
    :show="showModalAddGame"
    :title="t('collection.modals.titles.add_game').toUpperCase()"
    @close="showModalAddGame = false"
  >
    <template #content>
      <div class="game-add">
        <div class="game-search">
          <base-input
            v-model="gameSearch"
            background="#fff"
            color="#000"
            :placeholder="t('common.labels.search')"
            @key:enter="searchGame()"
          >
            <template #sufix>
              <div class="icon">
                <svg-icon icon="search" size="1.75rem" @click="searchGame()" />
              </div>
            </template>
          </base-input>
        </div>

        <div
          ref="gameSearchListRef"
          class="game-list"
          @scroll="onGameListScroll"
        >
          <template v-for="(item, index) in gameSearchList" :key="index">
            <div class="game">
              <base-avatar
                position="center"
                :is-edit="false"
                :show-image="true"
                :url="item?.avatar?.large"
                :aspect-ratio="1.333"
                size="3rem"
                border-radius="0.25rem"
              ></base-avatar>

              <div class="name">
                {{ item.name }}
              </div>
              <label class="custom-checkbox" :for="`game-${item.game_id}`">
                <input
                  :id="`game-${item.game_id}`"
                  class="checkbox-input"
                  type="checkbox"
                  @change="selectGame(item)"
                  :checked="gameIds.some((g) => g.game_id === item.game_id)"
                />
                <span class="checkbox-mark"></span>
              </label>
            </div>
          </template>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<style lang="scss" scoped>
.tab-console {
  display: flex;
  flex-direction: column;
  padding-block: 1rem;
  gap: 1rem;

  .tab-header {
    display: inline-flex;
    gap: 0.5rem;
    font-size: 1.2rem;
    align-items: center;
    padding-block-end: 0.75rem;
    text-transform: uppercase;
  }

  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .images {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }

    .list {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      align-items: flex-start;
      width: 100%;
      gap: 1.5rem;
      row-gap: 1.5rem;
      padding: 1rem;
      background: var(--white-off);
      border-radius: 0.25rem;

      .image {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        background: white;
        border-radius: 0.5rem;
        padding: 1rem;
        margin-block-end: 1.5rem;

        .avatar {
          display: flex;
          align-items: center;
          justify-content: center;
          width: v-bind("imageSize");
          height: auto;
          min-width: 3rem;
          min-height: 3rem;
          color: var(--text-medium);
          overflow: hidden;
        }
      }
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }

    .empty-data {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-block: 2rem;
      background: var(--white-off);
      border-radius: 0.5rem;
    }
  }
}

.game-add {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  .game-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    overflow-y: scroll;
    max-height: 20rem;
    height: 20rem;
    padding: 0.5rem;
    background: white;
    border-radius: 0.5rem;

    .game {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.5rem;
      padding-block: 0.5rem;
      padding-inline: 1rem;
      background: #f1f1f1;
      border-radius: 0.5rem;

      .custom-checkbox {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-inline-start: auto;
        cursor: pointer;
      }

      .checkbox-input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .checkbox-mark {
        width: 1.1rem;
        height: 1.1rem;
        border: 0.125rem solid var(--primary);
        border-radius: 0.25rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        background: white;
      }

      .checkbox-input:checked + .checkbox-mark {
        background: var(--primary);
        border-color: var(--primary);
      }

      .checkbox-input:checked + .checkbox-mark::after {
        content: "";
        width: 0.3rem;
        height: 0.6rem;
        border: solid white;
        border-width: 0 0.125rem 0.125rem 0;
        transform: rotate(45deg);
        margin-top: -0.1rem;
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .tab-console {
    .tab-content {
      padding-inline: 0;
    }
  }
}
</style>
